import React, { useState, useEffect } from 'react';
import { 
  Typography, Grid, Paper, Box, Button, 
  TableContainer, Table, TableHead, TableBody, TableRow, TableCell,
  Dialog, DialogTitle, DialogContent, DialogActions, DialogContentText,
  TextField, FormControl, InputLabel, Select, MenuItem,
  Chip, IconButton, Tooltip, LinearProgress, CircularProgress,
  Divider, Stack, Accordion, AccordionSummary, AccordionDetails,
  FormLabel, RadioGroup, FormControlLabel, Radio,
  Card, CardContent, Container,
  useTheme, alpha
} from '@mui/material';
import { usePermissions } from '../../../../utils/permissions';
import { 
  Add, Edit, Delete, Print, 
  ExpandMore, HelpOutline, Visibility,
  Science, Close,
  Science as ScienceIcon
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';

const TestRecords = ({ 
  formulation, 
  showSnackbar, 
  user, 
  tests, 
  setTests, 
  loading, 
  setLoading,
  fetchTests,
  canEdit: propCanEdit,
  canDelete: propCanDelete
}) => {
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  
  // State for user profile data
  const [userProfile, setUserProfile] = useState(null);
  
  // Use passed permissions or check via usePermissions for tests tab specifically
  const canEdit = propCanEdit !== undefined ? propCanEdit : hasPermission('products.formulations.tests.edit', 'write');
  const canDelete = propCanDelete !== undefined ? propCanDelete : hasPermission('products.formulations.tests.delete', 'delete');
  const canView = hasPermission('products.formulations.tests.view', 'read');
  
  // Accordion state management with localStorage
  const getStorageKey = (accordionType) => `testsTab_${formulation?.id}_${accordionType}_expanded`;
  
  const [testRecordsExpanded, setTestRecordsExpanded] = useState(() => {
    const stored = localStorage.getItem(getStorageKey('testRecords'));
    return stored ? JSON.parse(stored) : false;
  });

  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [currentTest, setCurrentTest] = useState(null);
  const [viewTest, setViewTest] = useState(null);
  
  // Regular test form data
  const [testFormData, setTestFormData] = useState({
    test_type: '',
    test_date: new Date().toISOString().split('T')[0],
    results: '',
    status: 'pending',
    notes: '',
    test_conditions: '',
    technician: '',
    test_batch_number: '',
    test_sample_id: '',
    test_location: '',
    is_passed: null,
    parameters: []
  });

  // Delete confirmation states
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteConfirmStep, setDeleteConfirmStep] = useState(1);
  const [testToDelete, setTestToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  // Update accordion state when formulation changes
  useEffect(() => {
    if (formulation?.id) {
      const testRecordsStored = localStorage.getItem(getStorageKey('testRecords'));
      setTestRecordsExpanded(testRecordsStored ? JSON.parse(testRecordsStored) : false);
    }
  }, [formulation?.id]);

  // Load user profile data
  useEffect(() => {
    const loadUserProfile = async () => {
      if (!user?.id) return;
      
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('first_name, last_name, full_name')
          .eq('id', user.id)
          .single();
        
        if (error) {
          console.error('Error loading user profile:', error);
          return;
        }
        
        setUserProfile(data);
      } catch (err) {
        console.error('Error loading user profile:', err);
      }
    };

    loadUserProfile();
  }, [user?.id]);

  // Save accordion states to localStorage
  const handleTestRecordsExpandChange = (event, isExpanded) => {
    setTestRecordsExpanded(isExpanded);
    if (formulation?.id) {
      localStorage.setItem(getStorageKey('testRecords'), JSON.stringify(isExpanded));
    }
  };
  


  const handleTestDialogOpen = (test = null) => {
    if (test) {
      setCurrentTest(test);
      setTestFormData({
        test_type: test.test_type,
        test_date: test.test_date,
        results: test.results,
        status: test.status,
        notes: test.notes || '',
        test_conditions: test.test_conditions || '',
        technician: test.technician || '',
        test_batch_number: test.test_batch_number || '',
        test_sample_id: test.test_sample_id || '',
        test_location: test.test_location || '',
        is_passed: test.is_passed,
        parameters: test.parameters ? 
          (typeof test.parameters === 'string' ? 
            JSON.parse(test.parameters) : test.parameters) : []
      });
    } else {
      setCurrentTest(null);
      setTestFormData({
        test_type: '',
        test_date: new Date().toISOString().split('T')[0],
        results: '',
        status: 'pending',
        notes: '',
        test_conditions: '',
        technician: userProfile?.first_name && userProfile?.last_name ? 
          `${userProfile.first_name} ${userProfile.last_name}` : 
          userProfile?.full_name || user?.email || '',
        is_passed: null,
        parameters: []
      });
    }
    setTestDialogOpen(true);
  };

  const handleViewDialogOpen = (test) => {
    setViewTest(test);
    setViewDialogOpen(true);
  };

  const handleTestChange = (e) => {
    const { name, value } = e.target;
    setTestFormData({
      ...testFormData,
      [name]: value
    });
  };

  const handlePassFailChange = (e) => {
    const value = e.target.value;
    let isPassed = null;
    let status = 'pending';
    
    if (value === 'pass') {
      isPassed = true;
      status = 'passed';
    } else if (value === 'fail') {
      isPassed = false;
      status = 'failed';
    } else if (value === 'na') {
      isPassed = null;
      status = 'completed';
    }
    
    setTestFormData({
      ...testFormData,
      is_passed: isPassed,
      status: status
    });
  };

  const handleAddParameter = () => {
    setTestFormData({
      ...testFormData,
      parameters: [
        ...testFormData.parameters,
        { name: '', value: '', unit: '' }
      ]
    });
  };

  const handleParameterChange = (index, field, value) => {
    const updatedParameters = [...testFormData.parameters];
    updatedParameters[index][field] = value;
    
    setTestFormData({
      ...testFormData,
      parameters: updatedParameters
    });
  };



  const handleRemoveParameter = (index) => {
    const updatedParameters = [...testFormData.parameters];
    updatedParameters.splice(index, 1);
    
    setTestFormData({
      ...testFormData,
      parameters: updatedParameters
    });
  };

  const renderParameterInput = (param, index) => {
    return (
      <TextField
        label="Value"
        value={param.value || ''}
        onChange={(e) => handleParameterChange(index, 'value', e.target.value)}
        size="small"
        multiline
        rows={3}
        sx={{ 
          mr: 1, 
          flexGrow: 1,
          '& .MuiOutlinedInput-root': {
            backgroundColor: 'white'
          }
        }}
        placeholder="Enter value..."
      />
    );
  };

  const checkForDuplicates = async () => {
    try {
      const errors = [];
      
      // Check for duplicate batch number
      if (testFormData.test_batch_number && testFormData.test_batch_number.trim()) {
        let batchQuery = supabase
          .from('formulation_tests')
          .select('id')
          .eq('formulation_id', formulation.id)
          .eq('test_batch_number', testFormData.test_batch_number.trim());
          
        // Only exclude current test if we're editing an existing one
        if (currentTest?.id) {
          batchQuery = batchQuery.neq('id', currentTest.id);
        }
          
        const { data: batchData, error: batchError } = await batchQuery;
        
        if (batchError) throw batchError;
        
        if (batchData && batchData.length > 0) {
          errors.push(`Batch Number "${testFormData.test_batch_number}" already exists for this formulation`);
        }
      }
      
      // Check for duplicate sample ID
      if (testFormData.test_sample_id && testFormData.test_sample_id.trim()) {
        let sampleQuery = supabase
          .from('formulation_tests')
          .select('id')
          .eq('formulation_id', formulation.id)
          .eq('test_sample_id', testFormData.test_sample_id.trim());
          
        // Only exclude current test if we're editing an existing one
        if (currentTest?.id) {
          sampleQuery = sampleQuery.neq('id', currentTest.id);
        }
          
        const { data: sampleData, error: sampleError } = await sampleQuery;
        
        if (sampleError) throw sampleError;
        
        if (sampleData && sampleData.length > 0) {
          errors.push(`Sample ID "${testFormData.test_sample_id}" already exists for this formulation`);
        }
      }
      
      return errors;
    } catch (error) {
      console.error('Error checking for duplicates:', error);
      throw error;
    }
  };

  const handleSaveTest = async () => {
    try {
      setLoading(true);
      
      if (!formulation?.id) {
        showSnackbar('No formulation selected', 'error');
        return;
      }
      
      if (!testFormData.test_type || !testFormData.test_date) {
        showSnackbar('Please fill in required fields', 'error');
        return;
      }
      
      // Check for duplicate batch number and sample ID
      const duplicateErrors = await checkForDuplicates();
      if (duplicateErrors.length > 0) {
        showSnackbar(duplicateErrors.join('. '), 'error');
        return;
      }
      
      const testData = {
        ...testFormData,
        parameters: testFormData.parameters.length > 0 ? testFormData.parameters : null
      };
      
      if (currentTest) {
        const { data, error } = await supabase
          .from('formulation_tests')
          .update({
            ...testData,
            updated_at: new Date(),
          })
          .eq('id', currentTest.id)
          .select();
          
        if (error) throw error;
        
        if (data && data[0]) {
          setTests(prevTests => 
            prevTests.map(test => test.id === data[0].id ? data[0] : test)
          );
          showSnackbar('Test updated successfully', 'success');
        } else {
          await fetchTests();
          showSnackbar('Test updated successfully', 'success');
        }
      } else {
        const { data, error } = await supabase
          .from('formulation_tests')
          .insert({
            ...testData,
            formulation_id: formulation.id,
            created_at: new Date(),
            created_by: user?.id
          })
          .select();
          
        if (error) throw error;
        
        if (data && data.length > 0) {
          setTests(prevTests => [...prevTests, data[0]]);
          showSnackbar('Test added successfully', 'success');
        } else {
          await fetchTests();
          showSnackbar('Test added successfully', 'success');
        }
      }
      
      setTestDialogOpen(false);
    } catch (error) {
      console.error('Error saving test:', error);
      showSnackbar('Error saving test: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteTest = async (test) => {
    setTestToDelete(test);
    setDeleteConfirmStep(1);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('formulation_tests')
        .delete()
        .eq('id', testToDelete.id);
        
      if (error) throw error;
      
      setTests(tests.filter(t => t.id !== testToDelete.id));
      
      setDeleteDialogOpen(false);
      setTestToDelete(null);
      
      showSnackbar('Test record deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting record:', error);
      showSnackbar('Error deleting record: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };
  
  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setDeleteConfirmStep(1);
    setTestToDelete(null);
    setDeleteConfirmText('');
  };

  const renderTestType = (type) => {
    if (!type) return 'Unknown Test Type';
    
    switch(type) {
      case 'stability': return 'Stability Test';
      case 'microbial': return 'Microbial Challenge Test';
      case 'preservative': return 'Preservative Efficacy Test';
      case 'compatibility': return 'Packaging Compatibility';
      case 'accelerated': return 'Accelerated Aging';
      case 'safety': return 'Safety Assessment';
      case 'sensory': return 'Sensory Evaluation';
      case 'ph': return 'pH Test';
      case 'viscosity': return 'Viscosity Test';
      case 'density': return 'Density/Specific Gravity';
      case 'color': return 'Color Analysis';
      case 'texture': return 'Texture Analysis';
      case 'emulsion': return 'Emulsion Stability';
      case 'freeze_thaw': return 'Freeze-Thaw Cycle';
      case 'certificate_of_analysis': return 'Certificate of Analysis';
      case 'other': return 'Other Test';
      default: 
        return type.charAt(0).toUpperCase() + type.slice(1).replace(/_/g, ' ');
    }
  };

  const getTestParameters = (test) => {
    try {
      if (!test.parameters) return "";
      
      const params = typeof test.parameters === 'string' 
        ? JSON.parse(test.parameters) 
        : test.parameters;
      
      if (!params || !Array.isArray(params) || params.length === 0) return "";
      
      return params
        .filter(p => p.name && p.value)
        .map(p => {
          let displayValue = p.value;
          if (p.unit) displayValue += ` ${p.unit}`;
          return `${p.name}: ${displayValue}`;
        })
        .join(', ');
    } catch (e) {
      console.error('Error parsing parameters:', e);
      return "";
    }
  };

  const renderParameterForView = (param) => {
    if (!param.name || !param.value) return null;
    
    let displayValue = param.value;
    if (param.unit) {
      displayValue += ` ${param.unit}`;
    }
    
    return (
      <Box key={param.name} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          {param.name}
        </Typography>
        <Typography variant="body2" sx={{ 
          bgcolor: 'grey.50', 
          p: 1, 
          borderRadius: 1,
          wordBreak: 'break-word',
          whiteSpace: 'pre-wrap'
        }}>
          {displayValue}
        </Typography>
      </Box>
    );
  };

  const handlePrintTestRecord = async (test) => {
    try {
      setLoading(true);
      
      const printCSS = `
        @page { 
          size: auto; 
          margin: 0; 
        }
        @media print {
          body { 
            padding: 20mm !important; 
            -webkit-print-color-adjust: exact;
          }
          .no-print { 
            display: none !important; 
          }
        }
        body {
          font-family: Arial, sans-serif;
          line-height: 1.3;
          color: #000;
          padding: 20px;
          max-width: 800px;
          margin: 0 auto;
        }
        .header {
          text-align: center;
          font-size: 16pt;
          font-weight: bold;
          margin-bottom: 10px;
        }
        .divider {
          border-top: 1px solid #000;
          margin: 5px 0;
        }
        .sub-header {
          font-size: 14pt;
          font-weight: bold;
          margin: 15px 0 5px 0;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 10px 0;
        }
        table, th, td {
          border: 1px solid #000;
        }
        th, td {
          padding: 6px 8px;
          text-align: left;
        }
        th {
          background-color: #f5f5f5;
          font-weight: bold;
        }
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          width: 100%;
          gap: 10px;
        }
        .info-section {
          margin-bottom: 15px;
        }
        .info-item {
          display: flex;
          margin: 4px 0;
        }
        .info-label {
          font-weight: bold;
          width: 140px;
          min-width: 140px;
        }
        .value-box {
          border: 1px solid #ddd;
          padding: 8px;
          margin: 4px 0;
          background-color: #f9f9f9;
        }
        .note-box {
          border: 1px solid #ddd;
          padding: 12px;
          margin: 8px 0;
          background-color: #f9f9f9;
          border-radius: 4px;
        }
        .status-pass {
          color: #2e7d32;
          font-weight: bold;
        }
        .status-fail {
          color: #d32f2f;
          font-weight: bold;
        }
        .status-pending {
          color: #ed6c02;
          font-weight: bold;
        }
        .status-other {
          color: #1976d2;
          font-weight: bold;
        }
        .timestamp {
          font-size: 9pt;
          color: #666;
          margin-top: 20px;
          text-align: right;
        }
        .param-item {
          margin-bottom: 8px;
        }
        .param-name {
          font-weight: bold;
          color: #333;
        }
      `;
      
      const getFormattedParameters = (test) => {
        try {
          if (!test.parameters) return "";
          
          const params = typeof test.parameters === 'string' 
            ? JSON.parse(test.parameters) 
            : test.parameters;
          
          if (!params || !Array.isArray(params) || params.length === 0) return "";
          
                      return params
              .filter(p => p.name && p.value)
              .map(p => {
                let displayValue = p.value;
                if (p.unit) {
                  displayValue += ` ${p.unit}`;
                }
                return `
                  <tr>
                    <td>
                      <span class="param-name">${p.name}</span>
                    </td>
                    <td style="white-space: pre-wrap;">${displayValue}</td>
                  </tr>
                `;
              })
              .join('');
        } catch (e) {
          console.error('Error parsing parameters:', e);
          return "";
        }
      };
      
      const getStatusClass = (status, isPassed) => {
        if (isPassed === true || status === 'passed') return 'status-pass';
        if (isPassed === false || status === 'failed') return 'status-fail';
        if (status === 'pending') return 'status-pending';
        return 'status-other';
      };
      
      const printHTML = `
        <div class="header">${renderTestType(test.test_type)} Report</div>
        <div class="divider"></div>
        
        <div class="info-section">
          <div class="info-grid">
            <div>
              <div class="info-item">
                <div class="info-label">Test Type:</div>
                <div><strong>${renderTestType(test.test_type)}</strong></div>
              </div>
              <div class="info-item">
                <div class="info-label">Test Date:</div>
                <div>${new Date(test.test_date).toLocaleDateString()}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Performed By:</div>
                <div>${test.technician || 'Not specified'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Batch Number:</div>
                <div>${test.test_batch_number || 'Not specified'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Sample ID:</div>
                <div>${test.test_sample_id || 'Not specified'}</div>
              </div>
            </div>
            <div>
              <div class="info-item">
                <div class="info-label">Formulation:</div>
                <div>${formulation?.name || 'Not specified'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Test Location:</div>
                <div>${test.test_location || 'Not specified'}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Status:</div>
                <div class="${getStatusClass(test.status, test.is_passed)}">
                  ${test.status.replace('_', ' ').toUpperCase()}
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Test Result:</div>
                <div>${test.is_passed === true ? 'PASS' : test.is_passed === false ? 'FAIL' : 'Not determined'}</div>
              </div>
            </div>
          </div>
        </div>
        
        ${test.test_conditions ? `
        <div class="sub-header">Test Conditions</div>
        <div class="divider"></div>
        <div class="value-box">
          ${test.test_conditions}
        </div>
        ` : ''}
        
        ${getFormattedParameters(test) ? `
        <div class="sub-header">Test Parameters</div>
        <div class="divider"></div>
        <table>
          <tr>
            <th style="width: 40%;">Parameter</th>
            <th style="width: 60%;">Value</th>
          </tr>
          ${getFormattedParameters(test)}
        </table>
        ` : ''}
        
        <div class="sub-header">Results</div>
        <div class="divider"></div>
        <div class="value-box">
          ${test.results || 'No detailed results provided'}
        </div>
        
        ${test.notes ? `
        <div class="sub-header">Notes</div>
        <div class="divider"></div>
        <div class="note-box">
          ${test.notes}
        </div>
        ` : ''}
        
        <div class="timestamp">
          Generated on ${new Date().toLocaleString()}<br>
          Test record created on ${new Date(test.created_at).toLocaleString()}
          ${test.updated_at ? `<br>Last updated on ${new Date(test.updated_at).toLocaleString()}` : ''}
        </div>
      `;
      
      let printFrame = document.getElementById('test-print-frame');
      if (printFrame) {
        document.body.removeChild(printFrame);
      }
      
      printFrame = document.createElement('iframe');
      printFrame.id = 'test-print-frame';
      printFrame.style.position = 'fixed';
      printFrame.style.top = '-9999px';
      printFrame.style.left = '-9999px';
      printFrame.style.width = '0';
      printFrame.style.height = '0';
      printFrame.style.border = '0';
      document.body.appendChild(printFrame);
      
      const frameDoc = printFrame.contentDocument || printFrame.contentWindow.document;
      
      frameDoc.open();
      frameDoc.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title></title>
          <style>${printCSS}</style>
        </head>
        <body>
          ${printHTML}
        </body>
        </html>
      `);
      frameDoc.close();
      
      setTimeout(() => {
        try {
          printFrame.contentWindow.focus();
          printFrame.contentWindow.print();
          
          setTimeout(() => {
            if (document.body.contains(printFrame)) {
              document.body.removeChild(printFrame);
            }
            setLoading(false);
          }, 1000);
        } catch (error) {
          console.error('Print error:', error);
          setLoading(false);
          showSnackbar('Error printing test report', 'error');
          if (document.body.contains(printFrame)) {
            document.body.removeChild(printFrame);
          }
        }
      }, 500);
      
    } catch (error) {
      console.error('Error generating test report:', error);
      showSnackbar('Error generating test report', 'error');
      setLoading(false);
    }
  };

  return (
    <>
      {/* Test Records Section */}
      <Accordion 
        expanded={testRecordsExpanded}
        elevation={0}
        sx={{
          mb: 2,
          borderRadius: 3,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: `linear-gradient(${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
          backdropFilter: 'blur(20px)',
          overflow: 'hidden',
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
          '&:before': {
            display: 'none'
          },
          '&.Mui-expanded': {
            margin: '0 0 16px 0'
          },
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
        onChange={handleTestRecordsExpandChange}
      >
        <AccordionSummary 
          expandIcon={<ExpandMore sx={{ color: theme.palette.primary.main }} />}
          sx={{
            borderRadius: '12px 12px 0 0',
            background: 'white',
            backdropFilter: 'blur(10px)',
            border: 'none',
            minHeight: 72,
            '&.Mui-expanded': {
              minHeight: 72,
              borderRadius: '12px 12px 0 0'
            },
            '& .MuiAccordionSummary-content': {
              margin: '16px 0',
              alignItems: 'center',
              '&.Mui-expanded': {
                margin: '16px 0'
              }
            }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ 
              p: 1, 
              borderRadius: 2, 
              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              color: theme.palette.primary.main,
              mr: 2
            }}>
              <ScienceIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                Test Records
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                Laboratory test results and quality control data
              </Typography>
            </Box>
            <Chip 
              label={`${tests.filter(test => test.test_type !== 'certificate_of_analysis').length} ${tests.filter(test => test.test_type !== 'certificate_of_analysis').length === 1 ? 'record' : 'records'}`}
              size="small"
              variant="outlined"
              sx={{ 
                fontWeight: 500,
                borderColor: theme.palette.primary.main,
                color: theme.palette.primary.main,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                mr: 2
              }}
            />
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {/* Action Button Section */}
          <Box sx={{ 
            p: 1.5, 
            borderBottom: `1px solid ${theme.palette.divider}`,
            display: 'flex',
            justifyContent: 'flex-end'
          }}>
            {canEdit && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleTestDialogOpen()}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  fontWeight: 500,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-1px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Add Test Record
              </Button>
            )}
          </Box>

          {/* Content Section */}
          <Box sx={{ p: 3 }}>
            {loading && <LinearProgress sx={{ mb: 2 }} />}
            
            {tests.filter(test => test.test_type !== 'certificate_of_analysis').length > 0 ? (
              <TableContainer 
                sx={{ 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Test Type
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Date
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Parameters
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Results
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Technician
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Status
                      </TableCell>
                      <TableCell 
                        align="right" 
                        sx={{ 
                          fontWeight: 500,
                          fontSize: '0.9rem',
                          bgcolor: 'white',
                          borderBottom: `1px solid ${theme.palette.primary.main}`,
                          color: 'primary.main',
                          py: 2,
                          px: 3
                        }}
                      >
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tests
                      .filter(test => test.test_type !== 'certificate_of_analysis')
                      .sort((a, b) => new Date(b.test_date) - new Date(a.test_date))
                      .map((test) => (
                        <TableRow 
                          key={test.id} 
                          hover 
                          sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              transition: 'all 0.2s ease',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 2.5,
                              px: 3
                            }
                          }}
                          onClick={() => handleViewDialogOpen(test)}
                        >
                          <TableCell>{renderTestType(test.test_type)}</TableCell>
                          <TableCell>{new Date(test.test_date).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Typography noWrap style={{ maxWidth: 150 }}>
                              {getTestParameters(test) || "—"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Tooltip title={test.results || "No results recorded"}>
                              <Typography noWrap style={{ maxWidth: 200 }}>
                                {test.results || '—'}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>{test.technician || '—'}</TableCell>
                          <TableCell>
                            <Chip 
                              size="small" 
                              label={test.status.replace('_', ' ')}
                              color={
                                test.status === 'passed' || test.is_passed === true ? 'success' : 
                                test.status === 'failed' || test.is_passed === false ? 'error' : 
                                test.status === 'in_progress' ? 'primary' : 
                                'default'
                              }
                            />
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Tooltip title="View">
                              <IconButton size="small" onClick={() => handleViewDialogOpen(test)}>
                                <Visibility fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canEdit && (
                              <Tooltip title="Edit">
                                <IconButton size="small" onClick={() => handleTestDialogOpen(test)}>
                                  <Edit fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip title="Print/View">
                              <IconButton 
                                size="small" 
                                color="primary" 
                                onClick={() => handlePrintTestRecord(test)}
                              >
                                <Print fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {canDelete && (
                              <Tooltip title="Delete">
                                <IconButton size="small" color="error" onClick={() => handleDeleteTest(test)}>
                                  <Delete fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
                              <Paper 
                elevation={0}
                sx={{ 
                  p: 4, 
                  textAlign: 'center',
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`
                }}
              >
                <Box sx={{ 
                  p: 2,
                  borderRadius: 3,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
                  display: 'inline-block',
                  mb: 2
                }}>
                  <ScienceIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                </Box>
                <Typography variant="h6" sx={{ fontWeight: 500, mb: 1.5, color: 'text.primary' }}>
                  No Test Records Yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 500, mx: 'auto', fontWeight: 500 }}>
                  Start documenting your formulation testing by adding your first test record.
                </Typography>
                {canEdit && (
                  <Button 
                    variant="contained" 
                    startIcon={<Add />} 
                    onClick={() => handleTestDialogOpen()}
                    sx={{ 
                      borderRadius: 3,
                      px: 4,
                      py: 1.5,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                      fontWeight: 500,
                      boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                        transform: 'translateY(-2px)',
                        boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    Add First Test Record
                  </Button>
                )}
              </Paper>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>

      {/* View Test Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false);
          setViewTest(null);
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { 
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            maxHeight: '90vh',
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)',
            bgcolor: alpha(theme.palette.grey[50], 0.95),
            backdropFilter: 'blur(10px)'
          }
        }}
      >
        {viewTest && (
          <>
            <DialogTitle sx={{ 
              pb: 1.5, 
              pt: 2,
              px: 2,
              fontSize: '1.25rem', 
              fontWeight: 500,
              color: 'text.primary',
              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              bgcolor: 'white'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{
                  p: 1,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }}>
                  <Science sx={{ fontSize: 20, color: 'primary.main' }} />
                </Box>
                <Box>
                  <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
                    Test Details
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
                    {renderTestType(viewTest.test_type)} - {new Date(viewTest.test_date).toLocaleDateString()}
                  </Typography>
                </Box>
              </Box>
              <IconButton 
                onClick={() => setViewDialogOpen(false)} 
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.grey[400], 0.1),
                    color: 'text.primary'
                  }
                }}
              >
                <Close />
              </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 0 }}>
              <Box sx={{ p: 3 }}>
                <Grid container spacing={3}>
                  {/* Basic Test Info */}
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      mb: 2,
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
                          Test Information
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                Type
                              </Typography>
                              <Chip
                                label={renderTestType(viewTest.test_type)}
                                color="primary"
                                size="medium"
                                icon={<Science />}
                                sx={{
                                  fontWeight: 500,
                                  border: `1px solid`,
                                  borderColor: theme.palette.primary.main
                                }}
                              />
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                Test Date
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {new Date(viewTest.test_date).toLocaleDateString()}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                Status
                              </Typography>
                              <Chip
                                label={viewTest.status.replace('_', ' ')}
                                color={
                                  viewTest.status === 'passed' || viewTest.is_passed === true ? 'success' : 
                                  viewTest.status === 'failed' || viewTest.is_passed === false ? 'error' : 
                                  viewTest.status === 'in_progress' ? 'primary' : 
                                  'default'
                                }
                                size="medium"
                                sx={{
                                  fontWeight: 500,
                                  border: `1px solid`,
                                  borderColor: 
                                    viewTest.status === 'passed' || viewTest.is_passed === true ? theme.palette.success.main : 
                                    viewTest.status === 'failed' || viewTest.is_passed === false ? theme.palette.error.main : 
                                    viewTest.status === 'in_progress' ? theme.palette.primary.main : 
                                    theme.palette.grey[400]
                                }}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Test Details */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      height: '100%',
                      minHeight: '220px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
                          Test Details
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Technician
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                            {viewTest.technician || 'Not specified'}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Test Location
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {viewTest.test_location || 'Not specified'}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Batch Information */}
                  <Grid item xs={12} md={6}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      height: '100%',
                      minHeight: '220px',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
                          Batch Information
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Batch Number
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, mb: 1 }}>
                            {viewTest.test_batch_number || 'Not specified'}
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                            Sample ID
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {viewTest.test_sample_id || 'Not specified'}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Test Conditions */}
                  {viewTest.test_conditions && (
                    <Grid item xs={12}>
                      <Card sx={{ 
                        borderRadius: 3, 
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'default',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}>
                        <CardContent sx={{ p: 4 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                            Test Conditions
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                            {viewTest.test_conditions}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Parameters */}
                  {viewTest.parameters && (
                    <Grid item xs={12}>
                      <Card sx={{ 
                        borderRadius: 3, 
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'default',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}>
                        <CardContent sx={{ p: 4 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                            Test Parameters
                          </Typography>
                          {(() => {
                            try {
                              const params = typeof viewTest.parameters === 'string' 
                                ? JSON.parse(viewTest.parameters) 
                                : viewTest.parameters;
                              
                              if (!params || !Array.isArray(params) || params.length === 0) {
                                return <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>No parameters recorded</Typography>;
                              }
                              
                              return (
                                <Grid container spacing={3}>
                                  {params
                                    .filter(p => p.name && p.value)
                                    .map((param, index) => (
                                      <Grid item xs={12} md={6} key={index}>
                                        <Box sx={{ mb: 2 }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 500, color: 'text.secondary', mb: 0.5 }}>
                                            {param.name}
                                          </Typography>
                                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                            {param.value}{param.unit ? ` ${param.unit}` : ''}
                                          </Typography>
                                        </Box>
                                      </Grid>
                                    ))}
                                </Grid>
                              );
                            } catch (e) {
                              return <Typography variant="body1" sx={{ fontWeight: 500, color: 'error.main' }}>Error parsing parameters</Typography>;
                            }
                          })()}
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Results */}
                  <Grid item xs={12}>
                    <Card sx={{ 
                      borderRadius: 3, 
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: 'white',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      cursor: 'default',
                      '&:hover': {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: 'primary.main' }}>
                          Test Results
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                          {viewTest.results || 'No results recorded'}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Notes */}
                  {viewTest.notes && (
                    <Grid item xs={12}>
                      <Card sx={{ 
                        borderRadius: 3, 
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        cursor: 'default',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                        }
                      }}>
                        <CardContent sx={{ p: 4 }}>
                          <Typography variant="h6" sx={{ fontWeight: 500, mb: 3, color: 'primary.main' }}>
                            Additional Notes
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500, whiteSpace: 'pre-wrap' }}>
                            {viewTest.notes}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </Box>
            </DialogContent>
            
            <DialogActions sx={{ p: 2, borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`, bgcolor: 'white' }}>
              <Button 
                onClick={() => setViewDialogOpen(false)}
                sx={{ 
                  borderRadius: 2,
                  fontWeight: 500,
                  color: 'text.secondary'
                }}
              >
                Close
              </Button>
              {canEdit && (
                <Button 
                  variant="outlined"
                  onClick={() => {
                    setViewDialogOpen(false);
                    handleTestDialogOpen(viewTest);
                  }}
                  startIcon={<Edit />}
                  sx={{ 
                    borderRadius: 2,
                    fontWeight: 500,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                    '&:hover': {
                      border: `1px solid ${theme.palette.primary.main}`,
                      bgcolor: alpha(theme.palette.primary.main, 0.05)
                    }
                  }}
                >
                  Edit Test
                </Button>
              )}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Test Dialog */}
      <Dialog 
        open={testDialogOpen} 
        onClose={() => setTestDialogOpen(false)} 
        maxWidth="lg" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          {currentTest ? 'Edit Test Record' : 'Add Test Record'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Test Type</InputLabel>
                <Select
                  name="test_type"
                  value={testFormData.test_type}
                  onChange={handleTestChange}
                  label="Test Type"
                  required
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  <MenuItem value="ph">pH Test</MenuItem>
                  <MenuItem value="viscosity">Viscosity Test</MenuItem>
                  <MenuItem value="density">Density/Specific Gravity</MenuItem>
                  <MenuItem value="stability">Stability Test</MenuItem>
                  <MenuItem value="microbial">Microbial Challenge Test</MenuItem>
                  <MenuItem value="preservative">Preservative Efficacy Test</MenuItem>
                  <MenuItem value="compatibility">Packaging Compatibility</MenuItem>
                  <MenuItem value="accelerated">Accelerated Aging</MenuItem>
                  <MenuItem value="safety">Safety Assessment</MenuItem>
                  <MenuItem value="sensory">Sensory Evaluation</MenuItem>
                  <MenuItem value="color">Color Analysis</MenuItem>
                  <MenuItem value="texture">Texture Analysis</MenuItem>
                  <MenuItem value="emulsion">Emulsion Stability</MenuItem>
                  <MenuItem value="freeze_thaw">Freeze-Thaw Cycle</MenuItem>
                  <MenuItem value="other">Other Test</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="test_date"
                label="Test Date"
                type="date"
                value={testFormData.test_date}
                onChange={handleTestChange}
                fullWidth
                margin="normal"
                required
                InputLabelProps={{ shrink: true }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="normal">
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={testFormData.status}
                  onChange={handleTestChange}
                  label="Status"
                  sx={{
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="in_progress">In Progress</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="passed">Passed</MenuItem>
                  <MenuItem value="failed">Failed</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
                        <Grid item xs={12} sm={6}>
              <TextField
                name="technician"
                label="Technician"
                value={testFormData.technician}
                onChange={handleTestChange}
                fullWidth
                margin="normal"
                placeholder="Name of person conducting the test"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="test_batch_number"
                label="Batch Number"
                value={testFormData.test_batch_number}
                onChange={handleTestChange}
                fullWidth
                margin="normal"
                placeholder="e.g., BATCH-001"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="test_sample_id"
                label="Sample ID"
                value={testFormData.test_sample_id}
                onChange={handleTestChange}
                fullWidth
                margin="normal"
                placeholder="e.g., SAMPLE-001"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="test_location"
                label="Test Location"
                value={testFormData.test_location}
                onChange={handleTestChange}
                fullWidth
                margin="normal"
                placeholder="e.g., Lab A, Room 101"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="test_conditions"
                label="Test Conditions"
                value={testFormData.test_conditions}
                onChange={handleTestChange}
                fullWidth
                margin="normal"
                multiline
                rows={2}
                placeholder="Temperature, humidity, equipment used, etc."
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ mb: 2, mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Test Parameters
                  <Tooltip title="Add specific measurements, observations, or parameters for this test">
                    <IconButton size="small" sx={{ ml: 1 }}>
                      <HelpOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Button 
                    size="small" 
                    startIcon={<Add />} 
                    onClick={handleAddParameter}
                    sx={{ ml: 2 }}
                  >
                    Add Parameter
                  </Button>
                </Typography>
                
                {testFormData.parameters.map((param, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2, gap: 1 }}>
                    <TextField
                      label="Parameter Name"
                      value={param.name}
                      onChange={(e) => handleParameterChange(index, 'name', e.target.value)}
                      sx={{ 
                        flexGrow: 1,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white'
                        }
                      }}
                      size="small"
                      multiline
                      rows={3}
                      placeholder="e.g., pH, Temperature, Notes"
                    />
                    
                    {renderParameterInput(param, index)}
                    
                    <TextField
                      label="Unit"
                      value={param.unit || ''}
                      onChange={(e) => handleParameterChange(index, 'unit', e.target.value)}
                      sx={{ 
                        width: '100px',
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white'
                        }
                      }}
                      size="small"
                      multiline
                      rows={3}
                      placeholder="e.g., °C, %"
                    />
                    
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => handleRemoveParameter(index)}
                      sx={{ mt: 0.5 }}
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                
 
                
                {testFormData.parameters.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    No parameters added yet. Add specific measurements, notes, or observations for this test.
                  </Typography>
                )}
              </Box>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl component="fieldset" margin="normal">
                <FormLabel component="legend" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                  Test Result
                </FormLabel>
                <RadioGroup
                  row
                  value={
                    testFormData.is_passed === true ? 'pass' : 
                    testFormData.is_passed === false ? 'fail' : 
                    'na'
                  }
                  onChange={handlePassFailChange}
                  sx={{ mt: 1 }}
                >
                  <FormControlLabel 
                    value="pass" 
                    control={<Radio sx={{ color: 'success.main', '&.Mui-checked': { color: 'success.main' } }} />} 
                    label="Pass" 
                  />
                  <FormControlLabel 
                    value="fail" 
                    control={<Radio sx={{ color: 'error.main', '&.Mui-checked': { color: 'error.main' } }} />} 
                    label="Fail" 
                  />
                  <FormControlLabel 
                    value="na" 
                    control={<Radio />} 
                    label="N/A" 
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="results"
                label="Results"
                value={testFormData.results}
                onChange={handleTestChange}
                fullWidth
                margin="normal"
                multiline
                rows={3}
                placeholder="Enter detailed test results and observations"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                value={testFormData.notes}
                onChange={handleTestChange}
                fullWidth
                margin="normal"
                multiline
                rows={2}
                placeholder="Additional notes, analysis, or recommendations"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setTestDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveTest} 
            variant="contained" 
            disabled={loading}
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={handleCancelDelete} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 3, 
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Delete sx={{ color: 'error.main', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
              Confirm Delete
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.primary', mt: 1 }}>
            Are you sure you want to delete this test record? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid', 
          borderColor: 'divider', 
          p: 2, 
          justifyContent: 'space-between' 
        }}>
          <Button 
            onClick={handleCancelDelete}
            sx={{ 
              px: 3,
              borderRadius: 3,
              py: 1.5
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleDeleteConfirm}
            sx={{
              px: 3,
              borderRadius: 3,
              py: 1.5,
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'error.dark',
                transform: 'translateY(-1px)'
              }
            }}
          >
            Delete Test Record
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TestRecords;